import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getBackendUrl } from '../config';

const BACKEND_URL = getBackendUrl();

const Analysis = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [timePeriod, setTimePeriod] = useState('day');
  const [activeTab, setActiveTab] = useState('energy');
  const [loading, setLoading] = useState(true);
  const [showDateModal, setShowDateModal] = useState(false);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [dateRange, setDateRange] = useState({
    from: new Date().toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });

  const getToken = () => localStorage.getItem('access_token');

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/companies`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setCompanies(response.data);
      if (response.data.length > 0) {
        setSelectedCompany((prev) => prev ?? response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // Mock data based on time period
  const getMockData = () => {
    const multiplier = timePeriod === 'day' ? 1 : timePeriod === 'month' ? 30 : 365;
    return {
      production: 552.9 * multiplier,
      consumption: 3900 * multiplier / 1000,
      selfConsumption: 552.9 * multiplier,
      selfConsumptionPercent: 100,
      export: 0,
      exportPercent: 0,
      selfSufficiency: 550 * multiplier,
      selfSufficiencyPercent: 14,
      gridPurchase: 3.39 * multiplier,
      gridPurchasePercent: 86,
      imported: 3.39 * multiplier,
      pvProduced: 552.9 * multiplier,
      discharged: 0,
      exported: 0,
      consumed: 3.94 * multiplier,
      charged: 0,
      energyBalance: -3387.1 * multiplier / 1000
    };
  };

  const data = getMockData();

  // Chart data for supply/load
  const getChartData = () => {
    if (timePeriod === 'day') {
      return {
        labels: Array.from({ length: 24 }, (_, i) => `${i}:00`),
        load: [50, 45, 40, 35, 80, 150, 250, 350, 400, 380, 320, 280, 260, 300, 350, 400, 380, 320, 250, 180, 120, 80, 60, 50],
        grid: [50, 45, 40, 35, 80, 150, 50, 0, 0, 0, 0, 0, 0, 50, 100, 150, 180, 200, 200, 180, 120, 80, 60, 50],
        solar: [0, 0, 0, 0, 0, 0, 200, 350, 400, 380, 320, 280, 260, 250, 250, 250, 200, 120, 50, 0, 0, 0, 0, 0],
        genPower: [0, 0, 0, 0, 0, 0, 180, 320, 380, 360, 300, 260, 240, 230, 230, 230, 180, 100, 40, 0, 0, 0, 0, 0]
      };
    }
    const length = timePeriod === 'month' ? 30 : 12;
    return {
      labels: timePeriod === 'month' ? Array.from({ length: 30 }, (_, i) => i + 1) :
              ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Qer', 'Kor', 'Gus', 'Sht', 'Tet', 'Nën', 'Dhj'],
      load: Array.from({ length }, () => Math.random() * 500 + 200),
      grid: Array.from({ length }, () => Math.random() * 200 + 100),
      solar: Array.from({ length }, () => Math.random() * 400 + 100),
      genPower: Array.from({ length }, () => Math.random() * 350 + 50)
    };
  };

  const chartData = getChartData();
  const maxChartVal = Math.max(...chartData.load, ...chartData.grid, ...chartData.solar);

  const formatValue = (val, unit = 'kWh') => {
    if (val >= 1000) return { value: (val / 1000).toFixed(2), unit: 'MWh' };
    return { value: val.toFixed(2), unit };
  };

  const tabs = [
    { id: 'energy', label: t('energy'), labelFull: t('energyAnalysis') },
    { id: 'consumption', label: t('consumption'), labelFull: t('consumptionAnalysis') },
    { id: 'revenue', label: t('revenue'), labelFull: t('revenueAnalysis') },
    { id: 'benefits', label: t('benefits'), labelFull: t('benefits') }
  ];

  const periods = [
    { id: 'day', label: t('day') },
    { id: 'month', label: t('month') },
    { id: 'year', label: t('year') }
  ];

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-full"><p className="text-gray-500">{t('loading')}</p></div></Layout>;
  }

  return (
    <Layout>
      <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900 overflow-auto">
        {/* Header - Responsive */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-3 md:p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                </svg>
              </button>
              <div>
                <h1 className="text-base md:text-xl font-bold text-gray-900 dark:text-white">{t('energyManagement')}</h1>
                <p className="text-xs md:text-sm text-gray-500">{selectedCompany?.name}</p>
              </div>
            </div>
            
            {/* Company Selector: mobile = custom dropdown (si fotoja e dytë: pa kufi/hijë, bg i errët, zgjedhur gjelbër), desktop = native select */}
            <div className="relative w-full md:w-auto">
              <div className="md:hidden">
                <button
                  type="button"
                  onClick={() => setShowCompanyDropdown(!showCompanyDropdown)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-[#2d3a4f] text-left text-sm text-white font-medium"
                >
                  <span>{selectedCompany?.name || (companies.length === 0 ? t('noCompanies') : t('select'))}</span>
                  <svg className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${showCompanyDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showCompanyDropdown && (
                  <>
                    <button type="button" aria-label={t('close')} className="fixed inset-0 z-40 md:hidden" onClick={() => setShowCompanyDropdown(false)} />
                    <div className="absolute top-full left-0 right-0 mt-1 rounded-xl z-50 overflow-hidden bg-[#2d3a4f]">
                      {companies.map(c => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => {
                            setSelectedCompany(c);
                            setShowCompanyDropdown(false);
                          }}
                          className={`w-full text-left px-4 py-3 text-sm transition ${
                            String(selectedCompany?.id) === String(c.id)
                              ? 'bg-[#334155] text-emerald-400 font-medium'
                              : 'text-slate-200 hover:bg-[#334155]/70'
                          }`}
                        >
                          {c.name}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <select
                value={selectedCompany?.id || ''}
                onChange={(e) => setSelectedCompany(companies.find(c => c.id === e.target.value))}
                className="hidden md:block px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm w-auto"
              >
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Tabs - Fit perfectly on mobile, no scroll */}
          <div className="flex mt-3">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 pb-2 text-[11px] md:text-sm font-medium transition border-b-2 text-center ${
                  activeTab === tab.id 
                    ? 'text-purple-600 border-purple-600' 
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                <span className="md:hidden">{tab.label}</span>
                <span className="hidden md:inline">{tab.labelFull}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-3 md:p-4 overflow-auto">
          {/* Time Period Selector - Responsive */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 mb-4">
            <h2 className="text-sm md:text-lg font-semibold text-gray-900 dark:text-white">{t('energyManagement')}</h2>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Period Buttons */}
              <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                {periods.map(p => (
                  <button
                    key={p.id}
                    onClick={() => setTimePeriod(p.id)}
                    className={`px-3 md:px-4 py-1.5 rounded-md text-xs font-medium transition ${
                      timePeriod === p.id 
                        ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow' 
                        : 'text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              
              {/* Date Range Button */}
              <button 
                onClick={() => setShowDateModal(true)}
                className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-medium flex items-center gap-1 hover:bg-purple-700 transition"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="hidden md:inline">{t('selectDates')}</span>
              </button>
            </div>
          </div>

          {/* Stats Grid - Responsive */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-4">
            {/* Energy Balance */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-3 md:p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-center gap-4 mb-3">
                <div className="w-16 h-16 md:w-20 md:h-20 relative">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle cx="50%" cy="50%" r="45%" stroke="#e5e7eb" strokeWidth="6" fill="none" />
                    <circle cx="50%" cy="50%" r="45%" stroke="#f97316" strokeWidth="6" fill="none" 
                      strokeDasharray="220" strokeDashoffset="0" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-3 h-3 md:w-4 md:h-4 bg-orange-500 rounded-full"></div>
                  </div>
                </div>
                <div className="w-5 h-5 md:w-6 md:h-6 bg-purple-500 rounded-full"></div>
              </div>
              <div className="text-center">
                <p className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">{formatValue(Math.abs(data.energyBalance * 1000)).value} {formatValue(Math.abs(data.energyBalance * 1000)).unit}</p>
                <p className="text-xs md:text-sm text-gray-500">{t('energyBalance')}</p>
              </div>
            </div>

            {/* Production */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-3 md:p-4 border border-gray-200 dark:border-gray-700">
              <p className="text-xl md:text-3xl font-bold text-gray-900 dark:text-white">{formatValue(data.production).value} <span className="text-sm md:text-lg font-normal text-gray-500">{formatValue(data.production).unit}</span></p>
              <p className="text-xs md:text-sm text-gray-500 mb-2">{t('productionLabel')}</p>
              
              <div className="mb-2">
                <div className="h-6 md:h-8 bg-green-500 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-xs md:text-sm">{data.selfConsumptionPercent}%</span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-xs md:text-sm">
                <div>
                  <p className="text-gray-500">{t('selfConsumption')}</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{formatValue(data.selfConsumption).value} {formatValue(data.selfConsumption).unit}</p>
                </div>
                <div>
                  <p className="text-gray-500">{t('exportLabel')}</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{data.export} kWh</p>
                </div>
              </div>
            </div>

            {/* Consumption */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-3 md:p-4 border border-gray-200 dark:border-gray-700">
              <p className="text-xl md:text-3xl font-bold text-gray-900 dark:text-white">{formatValue(data.consumption * 1000).value} <span className="text-sm md:text-lg font-normal text-gray-500">{formatValue(data.consumption * 1000).unit}</span></p>
              <p className="text-xs md:text-sm text-gray-500 mb-2">{t('consumption')}</p>
              
              <div className="mb-2 flex h-6 md:h-8 rounded-lg overflow-hidden">
                <div className="bg-green-500 flex items-center justify-center" style={{ width: `${data.selfSufficiencyPercent}%` }}>
                  <span className="text-white font-bold text-[10px] md:text-xs">{data.selfSufficiencyPercent}%</span>
                </div>
                <div className="bg-cyan-500 flex items-center justify-center flex-1">
                  <span className="text-white font-bold text-[10px] md:text-xs">{data.gridPurchasePercent}%</span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-xs md:text-sm">
                <div>
                  <p className="text-gray-500">{t('selfSufficiency')}</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{formatValue(data.selfSufficiency).value} kWh</p>
                </div>
                <div>
                  <p className="text-gray-500">{t('buyFromGrid')}</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{formatValue(data.gridPurchase * 1000).value} {formatValue(data.gridPurchase * 1000).unit}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Chart and Supply/Usage - Responsive */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
            {/* Chart */}
            <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl p-3 md:p-4 border border-gray-200 dark:border-gray-700">
              <p className="text-xs md:text-sm text-gray-500 mb-2">Furnizimi (kW)</p>
              
              <div className="h-40 md:h-64 flex items-end relative">
                <div className="absolute left-0 top-0 bottom-0 w-6 md:w-8 flex flex-col justify-between text-[8px] md:text-[10px] text-gray-400">
                  <span>600</span>
                  <span>400</span>
                  <span>200</span>
                  <span>0</span>
                  <span>200</span>
                  <span>400</span>
                  <span>600</span>
                </div>
                
                <div className="flex-1 ml-7 md:ml-10 h-full flex flex-col">
                  <div className="flex-1 flex items-end gap-px">
                    {chartData.solar.map((val, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center justify-end">
                        <div className="w-full flex items-end gap-px">
                          <div className="flex-1 bg-blue-200 dark:bg-blue-400/30" style={{ height: `${(val / maxChartVal) * 100}%` }}></div>
                          <div className="flex-1 bg-green-400" style={{ height: `${(chartData.genPower[i] / maxChartVal) * 100}%` }}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="h-px bg-gray-300 dark:bg-gray-600"></div>
                  
                  <div className="flex-1 flex items-start gap-px">
                    {chartData.load.map((val, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center">
                        <div className="w-full flex items-start gap-px">
                          <div className="flex-1 bg-red-300 dark:bg-red-400/50" style={{ height: `${(val / maxChartVal) * 100}%` }}></div>
                          <div className="flex-1 bg-blue-400" style={{ height: `${(chartData.grid[i] / maxChartVal) * 100}%` }}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <p className="text-xs md:text-sm text-gray-500 text-center mt-2">Përdorimi</p>
              
              <div className="flex justify-center gap-3 md:gap-6 mt-2 flex-wrap">
                <div className="flex items-center gap-1"><div className="w-2 h-2 md:w-3 md:h-3 bg-red-300 rounded"></div><span className="text-[10px] md:text-xs text-gray-500">Ngarkesa</span></div>
                <div className="flex items-center gap-1"><div className="w-2 h-2 md:w-3 md:h-3 bg-blue-400 rounded"></div><span className="text-[10px] md:text-xs text-gray-500">Rrjeti</span></div>
                <div className="flex items-center gap-1"><div className="w-2 h-2 md:w-3 md:h-3 bg-blue-200 rounded"></div><span className="text-[10px] md:text-xs text-gray-500">Diellore</span></div>
                <div className="flex items-center gap-1"><div className="w-2 h-2 md:w-3 md:h-3 bg-green-400 rounded"></div><span className="text-[10px] md:text-xs text-gray-500">Gjenerimi</span></div>
              </div>
            </div>

            {/* Supply & Usage Cards */}
            <div className="space-y-3 md:space-y-4">
              {/* Supply */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-3 md:p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="text-xs md:text-sm font-semibold text-gray-900 dark:text-white mb-2 md:mb-3">Furnizimi</h3>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="w-8 h-8 md:w-10 md:h-10 mx-auto bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center mb-1">
                      <svg className="w-4 h-4 md:w-5 md:h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                    <p className="text-xs md:text-sm font-bold text-gray-900 dark:text-white">{formatValue(data.imported * 1000).value}</p>
                    <p className="text-[8px] md:text-[10px] text-gray-500">Importuar</p>
                  </div>
                  <div>
                    <div className="w-8 h-8 md:w-10 md:h-10 mx-auto bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mb-1">
                      <svg className="w-4 h-4 md:w-5 md:h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </div>
                    <p className="text-xs md:text-sm font-bold text-gray-900 dark:text-white">{formatValue(data.pvProduced).value}</p>
                    <p className="text-[8px] md:text-[10px] text-gray-500">PV Prodhuar</p>
                  </div>
                  <div>
                    <div className="w-8 h-8 md:w-10 md:h-10 mx-auto bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mb-1">
                      <svg className="w-4 h-4 md:w-5 md:h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <p className="text-xs md:text-sm font-bold text-gray-900 dark:text-white">{data.discharged}</p>
                    <p className="text-[8px] md:text-[10px] text-gray-500">Shkarkuar</p>
                  </div>
                </div>
              </div>

              {/* Usage */}
              <div className="bg-white dark:bg-gray-800 rounded-xl p-3 md:p-4 border border-gray-200 dark:border-gray-700">
                <h3 className="text-xs md:text-sm font-semibold text-gray-900 dark:text-white mb-2 md:mb-3">Përdorimi</h3>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="w-8 h-8 md:w-10 md:h-10 mx-auto bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center mb-1">
                      <svg className="w-4 h-4 md:w-5 md:h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                      </svg>
                    </div>
                    <p className="text-xs md:text-sm font-bold text-gray-900 dark:text-white">{data.exported}</p>
                    <p className="text-[8px] md:text-[10px] text-gray-500">Eksportuar</p>
                  </div>
                  <div>
                    <div className="w-8 h-8 md:w-10 md:h-10 mx-auto bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center mb-1">
                      <svg className="w-4 h-4 md:w-5 md:h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    </div>
                    <p className="text-xs md:text-sm font-bold text-gray-900 dark:text-white">{formatValue(data.consumed * 1000).value}</p>
                    <p className="text-[8px] md:text-[10px] text-gray-500">Konsumuar</p>
                  </div>
                  <div>
                    <div className="w-8 h-8 md:w-10 md:h-10 mx-auto bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mb-1">
                      <svg className="w-4 h-4 md:w-5 md:h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                    </div>
                    <p className="text-xs md:text-sm font-bold text-gray-900 dark:text-white">{data.charged}</p>
                    <p className="text-[8px] md:text-[10px] text-gray-500">Ngarkuar</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Date Range Modal - mobile: në mes, X */}
        {showDateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 w-full max-w-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-bold text-gray-900 dark:text-white">Zgjidh Periudhën</h3>
                <button type="button" onClick={() => setShowDateModal(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400" aria-label="Mbyll">✕</button>
              </div>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Nga Data</label>
                  <input 
                    type="date" 
                    value={dateRange.from}
                    onChange={(e) => setDateRange({...dateRange, from: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Deri në Datën</label>
                  <input 
                    type="date"
                    value={dateRange.to}
                    onChange={(e) => setDateRange({...dateRange, to: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  />
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                <button 
                  onClick={() => setShowDateModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium"
                >
                  Anulo
                </button>
                <button 
                  onClick={() => {
                    setShowDateModal(false);
                    // Here you would fetch data for the selected date range
                  }}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700"
                >
                  Apliko
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Analysis;
